import {
  Button,
  CircularProgress,
  Grid,
  InputLabel,
  Typography,
  TextField,
  Checkbox,
  Box,
  Paper,
  Tooltip,
  IconButton
} from '@mui/material'
import React, { Fragment, useEffect, useState } from 'react'
import { ICreateDun } from '../../../interfaces/StockKeepingUnitDun/StockKeepingUnitDun'
import { TypePackage } from '../entities/Product'
import { useApi } from '../../../hooks/useApi'
import {
  deleteStockkeepingUnitDun,
  getListDunPage,
  getTypePackage,
  postStockKeepingUnitDun,
  putStockKeepingUnitDun
} from '../api'
import { GenericDLL } from '../../../components/Almacen/UI/GenericDLL'
import { ApiResponse } from '../../../typings/ApiResponse'
import { GenericTable } from '../../../components/_EOS/Table'
import { usePagination } from '../../../hooks/usePagination'
import { Status } from '../../../hooks/useFetchState'
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon
} from '@mui/icons-material'
import { CustomActionsProps, generateTableHeadersProduct } from './config'
import { Modal } from '../../../components/_EOS/Modal'
import { useModal } from '../../../hooks/useModal'

export interface ICreateDunProps {
  sku: string
  nombreProducto: string
  handleSnackbar: (message: string, success: boolean) => void
  handleCloseModal: () => void
}

export const CreateDun = ({
  sku,
  nombreProducto,
  handleSnackbar,
  handleCloseModal
}: ICreateDunProps) => {
  const [errors, setErrors] = useState<{
    [key: string]: string
  }>({})

  const confirmModal = useModal<number>('Confirmar')

  // states Form
  const [showForm, setShowForm] = useState(false)
  const [editingDun, setEditingDun] = useState<ICreateDun | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [loading, setLoading] = useState(false)

  // call Apis
  const { state: stateTypePackage, callApi: callApiTypePackage } =
    useApi<TypePackage[]>()
  const { state: stateDuns, callApi: callApiListDun } =
    useApi<ApiResponse<ICreateDun>>()
  const { callApi: callApiDelete } = useApi()
  const { callApi: callApiPut } = useApi()
  const { callApi: callApiPost } = useApi()

  const pagination = usePagination(5)
  const tableHeadersProduct = generateTableHeadersProduct(stateTypePackage.data)

  // Validar que solo haya un DUN predeterminado
  const validateDefaultDun = (dunToValidate: ICreateDun): boolean => {
    if (!dunToValidate.isDefault) return true

    // Obtener todos los DUNs existentes en la grilla (excluyendo el que se está editando)
    const existingDuns = stateDuns.data?.items || []
    const existingDefaultDuns = existingDuns.filter(
      (dun) =>
        dun.isDefault &&
        dun.stockKeepingUnitDunId !== dunToValidate.stockKeepingUnitDunId
    )

    return existingDefaultDuns.length === 0
  }

  const validateForm = (values: ICreateDun): boolean => {
    const newErrors: { [key: string]: string } = {}
    let isValid = true

    // Validar Dun - 14 caracteres obligatorio
    if (!values.dun || values.dun.trim() === '') {
      newErrors.dun = 'El DUN es obligatorio'
      isValid = false
    } else if (values.dun.trim().length !== 14) {
      newErrors.dun = 'El DUN debe tener exactamente 14 caracteres'
      isValid = false
    }

    // Validar cantidad mayor a cero
    if (!values.quantity || values.quantity <= 0) {
      newErrors.quantity = 'La cantidad debe ser mayor a cero'
      isValid = false
    }

    // Validar unidad de medida
    if (
      !values.typePackageId ||
      values.typePackageId === 0 ||
      values.typePackageId === -1
    ) {
      newErrors.typePackageId = 'La unidad de medida es obligatoria'
      isValid = false
    }

    setErrors(newErrors)
    return isValid
  }

  const handleSubmit = () => {
    if (!editingDun) return

    setIsSubmitting(true)

    // Primero validar el formulario básico
    if (!validateForm(editingDun)) {
      handleSnackbar(
        'Por favor, complete todos los campos requeridos correctamente',
        false
      )
      setIsSubmitting(false)
      return
    }

    // Validar que solo haya un DUN predeterminado
    if (editingDun.isDefault && !validateDefaultDun(editingDun)) {
      handleSnackbar(
        'Ya existe un DUN predeterminado. Solo puede haber uno por producto.',
        false
      )
      setIsSubmitting(false)
      return
    }

    // Crear array con el unico DUN para guardar
    const dunToSave = [editingDun]

    if (editingDun.stockKeepingUnitDunId === 0) {
      requestCreate(dunToSave)
    } else {
      requestUpdate(dunToSave)
    }
  }

  const requestUpdate = (dun: ICreateDun[]) => {
    callApiPut(putStockKeepingUnitDun(dun))
      .then(() => {
        handleSnackbar('Se ha actualizado correctamente.', true)
        resetForm()
        requestGetDuns(0, pagination.pageSize)
      })
      .catch((e) => {
        handleSnackbar(e, false)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const requestCreate = (dun: ICreateDun[]) => {
    callApiPost(postStockKeepingUnitDun(dun))
      .then(() => {
        handleSnackbar('Se ha creado correctamente.', true)
        resetForm()
        requestGetDuns(0, pagination.pageSize)
      })
      .catch((e) => {
        handleSnackbar(e, false)
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  const handleDelete = (stockKeepingUnitDunId: number) => {
    setLoading(true)
    callApiDelete(deleteStockkeepingUnitDun(stockKeepingUnitDunId))
      .then(() => {
        handleSnackbar('Se ha eliminado el DUN correctamente', true)
        requestGetDuns(0, pagination.pageSize)
      })
      .catch((e) => {
        handleSnackbar(e, false)
      })
      .finally(() => {
        setLoading(false)
        confirmModal.closeModal()
      })
  }

  const getTypePackages = async () => {
    callApiTypePackage(getTypePackage())
  }

  const handleDunChange = (value: string) => {
    if (!editingDun) return

    // valida 14 caracteres y convertir a mayusculas
    const cleanedValue = value.toUpperCase().slice(0, 14)

    const updatedDun = {
      ...editingDun,
      dun: cleanedValue
    }
    setEditingDun(updatedDun)

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors.dun) {
      const newErrors = { ...errors }
      delete newErrors.dun
      setErrors(newErrors)
    }
  }

  const handleInputChange = (field: keyof ICreateDun, value: any) => {
    if (!editingDun) return

    const updatedDun = {
      ...editingDun,
      [field]: value
    }
    setEditingDun(updatedDun)

    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      const newErrors = { ...errors }
      delete newErrors[field]
      setErrors(newErrors)
    }
  }

  const handleAddDun = () => {
    const newDun: ICreateDun = {
      stockKeepingUnitDunId: 0,
      dun: `${sku}`.slice(0, 14), // validar que no tenga mas de 14 caracteres
      quantity: 1,
      description: '',
      isDefault: false,
      stockKeepingUnitId: 0,
      sku: sku,
      typePackageId: -1
    }
    setEditingDun(newDun)
    setShowForm(true)
    setErrors({})
  }

  const handleDefaultChange = (checked: boolean) => {
    if (!editingDun) return

    // Si está marcando como predeterminado, validar que no exista otro
    if (checked && !validateDefaultDun({ ...editingDun, isDefault: true })) {
      handleSnackbar(
        'Ya existe un DUN predeterminado. Solo puede haber uno por producto.',
        false
      )
      return
    }

    const updatedDun = {
      ...editingDun,
      isDefault: checked
    }
    setEditingDun(updatedDun)
  }

  const handleEdit = (row: ICreateDun) => {
    setEditingDun({ ...row })
    setShowForm(true)
    setErrors({})
  }

  const resetForm = () => {
    setEditingDun(null)
    setShowForm(false)
    setErrors({})
  }

  const handleCancel = () => {
    resetForm()
  }

  useEffect(() => {
    requestGetDuns(0, pagination.pageSize)
    getTypePackages()
  }, [])

  const MyCustomActions: React.FC<CustomActionsProps> = ({ row }) => {
    return (
      <Fragment>
        <Tooltip title='Editar' aria-label='edit'>
          <IconButton
            disabled={showForm}
            aria-label='edit'
            onClick={() => handleEdit(row)}
            style={{ verticalAlign: 'bottom' }}
          >
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Eliminar' aria-label='delete'>
          <IconButton
            disabled={showForm}
            aria-label='delete'
            onClick={() =>
              confirmModal.openModal('xs', row.stockKeepingUnitDunId)
            }
            style={{ verticalAlign: 'bottom' }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Fragment>
    )
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    requestGetDuns(auxPage, pageSize)

    pagination.setPagination(auxPage, pageSize)
  }

  const requestGetDuns = (page?: number, pageSize?: number) => {
    callApiListDun(getListDunPage(sku, page, pageSize))
  }

  const handleCloseForm = () => {
    resetForm()
    handleCloseModal()
  }

  return (
    <Fragment>
      <Box style={{ paddingBottom: '20px' }}>
        <Typography variant='h6'>{nombreProducto}</Typography>
        <Typography variant='body2'>{sku}</Typography>
      </Box>
      {stateDuns.data?.items && stateDuns.data?.items.length > 0 && (
        <GenericTable<ICreateDun>
          dense={true}
          headersConfig={tableHeadersProduct}
          IDKey={'stockKeepingUnitDunId'}
          data={stateDuns.data?.items || []}
          pagination={{
            totalItem: stateDuns.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={stateDuns.status === Status.Loading}
          showCheckboxes={false}
          customActionComponent={MyCustomActions}
        />
      )}

      <Grid
        container
        spacing={2}
        style={{ marginTop: '10px', marginBottom: '10px' }}
        justifyContent='space-between'
      >
        <Grid item>
          <Button
            onClick={handleAddDun}
            variant='contained'
            color='secondary'
            startIcon={<AddIcon />}
          >
            Agregar DUN
          </Button>
        </Grid>
        <Grid item>
          <Button onClick={handleCloseForm} variant='outlined' color='primary'>
            CERRAR
          </Button>
        </Grid>
      </Grid>

      {showForm && editingDun && (
        <Paper style={{ padding: '20px', marginTop: '20px' }}>
          <Typography variant='h6' style={{ marginBottom: '20px' }}>
            {editingDun.stockKeepingUnitDunId === 0
              ? 'Nuevo DUN'
              : 'Editar DUN'}
          </Typography>

          <form autoComplete='off'>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3} md={3} lg={2}>
                <TextField
                  fullWidth
                  value={editingDun.dun || ''}
                  onChange={(e) => handleDunChange(e.target.value)}
                  label='DUN'
                  type='text'
                  disabled={isSubmitting}
                  error={!!errors.dun}
                  helperText={
                    errors.dun || `${editingDun.dun?.length || 0}/14 caracteres`
                  }
                  variant='outlined'
                  inputProps={{
                    maxLength: 14,
                    style: { textTransform: 'uppercase' }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={2} lg={2}>
                <TextField
                  fullWidth
                  value={editingDun.quantity || ''}
                  onChange={(e) =>
                    handleInputChange('quantity', parseInt(e.target.value) || 0)
                  }
                  label='Cantidad'
                  type='number'
                  disabled={isSubmitting}
                  error={!!errors.quantity}
                  helperText={errors.quantity}
                  inputProps={{ min: 1 }}
                  variant='outlined'
                />
              </Grid>

              <Grid item xs={12} sm={3} md={3} lg={4}>
                <TextField
                  fullWidth
                  value={editingDun.description || ''}
                  onChange={(e) =>
                    handleInputChange('description', e.target.value)
                  }
                  label='Descripción'
                  type='text'
                  disabled={isSubmitting}
                  variant='outlined'
                />
              </Grid>

              <Grid item xs={12} sm={3} md={3} lg={3}>
                <GenericDLL
                  fullWidth
                  initialValues={stateTypePackage.data || []}
                  onChange={(event: any) => {
                    const value = event.target.value
                    handleInputChange(
                      'typePackageId',
                      value === -1 ? '' : value
                    )
                  }}
                  title='Unidad de Medida'
                  name='name'
                  valueKey={'typePackageId'}
                  value={editingDun.typePackageId || ''}
                  variant='outlined'
                  error={!!errors.typePackageId}
                  helperText={errors.typePackageId}
                />
              </Grid>

              <Grid item xs={12} sm={3} md={2} lg={1}>
                <InputLabel style={{ fontSize: '12px' }} id='default'>
                  Predeterminado
                </InputLabel>
                <Checkbox
                  checked={editingDun.isDefault || false}
                  onChange={(e) => handleDefaultChange(e.target.checked)}
                  disabled={isSubmitting}
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2} justifyContent='flex-end'>
                  <Grid item>
                    <Button
                      onClick={handleSubmit}
                      variant='contained'
                      color='primary'
                      disabled={isSubmitting}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size='0.9rem' />
                        ) : undefined
                      }
                    >
                      {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      onClick={handleCancel}
                      variant='outlined'
                      disabled={isSubmitting}
                    >
                      Cancelar
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
        </Paper>
      )}
      <Modal
        loading={loading}
        open={confirmModal.isOpen}
        title={confirmModal.title}
        onClose={confirmModal.closeModal}
        cancelButton={{
          action: confirmModal.closeModal,
          title: 'Cancelar'
        }}
        successButton={{
          action: () => handleDelete(confirmModal.extendedProps || 0),
          title: 'Guardar'
        }}
      >
        <Typography variant='h6'>
          ¿Está seguro que desea eliminar el DUN seleccionado?
        </Typography>
      </Modal>
    </Fragment>
  )
}
